import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Grid3X3, List, AlertTriangle } from 'lucide-react';
import { calendarApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ────────────────────────────────────────────────────────────────

function parseDT(str) {
  if (!str) return null;
  const s = str.length === 10 ? str + 'T00:00' : str;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmtTime(date) {
  if (!date) return '';
  return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getQueryRange(view, date) {
  if (view === 'month') {
    const y = date.getFullYear(), m = date.getMonth();
    return {
      start: new Date(y, m - 1, 25).toISOString().slice(0, 10),
      end: new Date(y, m + 2, 5).toISOString().slice(0, 10),
    };
  }
  if (view === 'week') {
    const mon = getMonday(date);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { start: mon.toISOString().slice(0, 10), end: sun.toISOString().slice(0, 10) };
  }
  // day
  return { start: date.toISOString().slice(0, 10), end: date.toISOString().slice(0, 10) };
}

const EVENT_STYLE = {
  rental:      { bg: 'bg-amber-100 border-amber-300',   text: 'text-amber-900',   dot: 'bg-amber-500' },
  appointment: { bg: 'bg-purple-100 border-purple-300', text: 'text-purple-900',  dot: 'bg-purple-500' },
};

function getStyle(event) {
  return EVENT_STYLE[event.form_type] || EVENT_STYLE.rental;
}

function getEventsForDay(events, day) {
  return events.filter(e => {
    const s = parseDT(e.start);
    const en = parseDT(e.end) || s;
    if (!s) return false;
    const ds = new Date(day); ds.setHours(0, 0, 0, 0);
    const de = new Date(day); de.setHours(23, 59, 59, 0);
    return s <= de && en >= ds;
  }).sort((a, b) => (parseDT(a.start) || 0) - (parseDT(b.start) || 0));
}

// ─── Month View ──────────────────────────────────────────────────────────────

function MonthView({ events, currentDate, onDayClick }) {
  const today = new Date();
  const y = currentDate.getFullYear(), m = currentDate.getMonth();
  const firstDay = new Date(y, m, 1);
  const startDay = new Date(firstDay);
  const dow = firstDay.getDay();
  startDay.setDate(firstDay.getDate() - (dow === 0 ? 6 : dow - 1));

  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(startDay); d.setDate(startDay.getDate() + i); return d;
  });

  const DAY_HEADERS = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-100">
        {DAY_HEADERS.map(d => (
          <div key={d} className="text-center text-xs font-semibold text-slate-500 py-3">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 divide-x divide-slate-100">
        {cells.map((d, i) => {
          const dayEvts = getEventsForDay(events, d);
          const isCurrentMonth = d.getMonth() === m;
          const isToday = isSameDay(d, today);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <div key={i} onClick={() => onDayClick(d)}
              className={`min-h-[90px] p-1.5 cursor-pointer hover:bg-emerald-50/40 transition-colors border-b border-slate-100
                ${!isCurrentMonth ? 'bg-slate-50/50' : ''}
                ${isWeekend && isCurrentMonth ? 'bg-slate-50/30' : ''}`}>
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold mb-1
                ${isToday ? 'bg-[#0f6c44] text-white' : isCurrentMonth ? 'text-slate-800' : 'text-slate-300'}`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5">
                {dayEvts.slice(0, 3).map(e => {
                  const c = getStyle(e);
                  return (
                    <div key={e.id} className={`${c.bg} ${c.text} border text-xs px-1.5 py-0.5 rounded-md truncate font-medium`}>
                      {e.resource}
                    </div>
                  );
                })}
                {dayEvts.length > 3 && (
                  <div className="text-xs text-slate-400 pl-1 font-medium">+{dayEvts.length - 3} lagi</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week View ───────────────────────────────────────────────────────────────

function WeekView({ events, currentDate, onDayClick, onOrderClick }) {
  const today = new Date();
  const mon = getMonday(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
  const DAY_SHORT = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
        {days.map((d, i) => (
          <div key={i} onClick={() => onDayClick(d)}
            className="text-center py-3 cursor-pointer hover:bg-emerald-50 transition-colors">
            <p className="text-xs text-slate-400 font-medium">{DAY_SHORT[i]}</p>
            <p className={`text-xl font-extrabold font-manrope mt-0.5 ${isSameDay(d, today) ? 'text-[#0f6c44]' : 'text-slate-800'}`}>
              {d.getDate()}
            </p>
            <p className="text-xs text-slate-400">{getEventsForDay(events, d).length > 0 ? `${getEventsForDay(events, d).length} jadwal` : ''}</p>
          </div>
        ))}
      </div>
      {/* Events */}
      <div className="grid grid-cols-7 divide-x divide-slate-100 min-h-64">
        {days.map((d, i) => {
          const dayEvts = getEventsForDay(events, d);
          return (
            <div key={i} className={`p-2 space-y-1.5 ${isSameDay(d, today) ? 'bg-emerald-50/30' : ''}`}>
              {dayEvts.length === 0
                ? <div className="text-xs text-slate-300 text-center pt-6">–</div>
                : dayEvts.map(e => {
                  const c = getStyle(e);
                  return (
                    <div key={e.id} onClick={() => onOrderClick(e.order_id)}
                      className={`${c.bg} ${c.text} border ${c.bg.replace('bg-', 'border-')} text-xs p-2 rounded-xl cursor-pointer hover:shadow-md transition-shadow`}>
                      <p className="font-bold truncate">{e.resource}</p>
                      <p className="opacity-80 truncate text-xs mt-0.5">{e.customer_name}</p>
                      <p className="opacity-60 text-xs">{fmtTime(parseDT(e.start))}</p>
                    </div>
                  );
                })
              }
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Day View (Resource Timeline) ───────────────────────────────────────────

const START_H = 6;
const END_H = 22;
const TOTAL_H = END_H - START_H;
const HOUR_PX = 64;
const TOTAL_PX = TOTAL_H * HOUR_PX;
const HOURS = Array.from({ length: TOTAL_H + 1 }, (_, i) => i + START_H);

function getEventPos(startStr, endStr) {
  const s = parseDT(startStr);
  if (!s) return null;
  const e = parseDT(endStr) || new Date(s.getTime() + 3600000); // default 1h
  const startMins = Math.max(0, (s.getHours() - START_H) * 60 + s.getMinutes());
  const endMins = Math.min(TOTAL_H * 60, (e.getHours() - START_H) * 60 + e.getMinutes());
  const top = startMins / 60 * HOUR_PX;
  const height = Math.max(24, (endMins - startMins) / 60 * HOUR_PX);
  return { top, height };
}

function DayView({ events, currentDate, onOrderClick }) {
  const { t } = useLanguage();
  const dayStr = currentDate.toISOString().slice(0, 10);

  const dayEvents = events.filter(e => {
    const s = parseDT(e.start);
    const en = parseDT(e.end) || s;
    if (!s) return false;
    const ds = new Date(currentDate); ds.setHours(0, 0, 0, 0);
    const de = new Date(currentDate); de.setHours(23, 59, 59, 0);
    return s <= de && en >= ds;
  });

  const resources = [...new Set(dayEvents.map(e => e.resource))].sort();

  // ── Conflict Detection: same resource, overlapping time ──────────────────
  const conflictIds = new Set();
  resources.forEach(r => {
    const col = dayEvents.filter(e => e.resource === r);
    for (let i = 0; i < col.length; i++) {
      for (let j = i + 1; j < col.length; j++) {
        const a = col[i], b = col[j];
        const as = parseDT(a.start), ae = parseDT(a.end) || new Date(as.getTime() + 3600000);
        const bs = parseDT(b.start), be = parseDT(b.end) || new Date(bs.getTime() + 3600000);
        if (as && bs && as < be && ae > bs) {
          conflictIds.add(a.id);
          conflictIds.add(b.id);
        }
      }
    }
  });
  const hasConflicts = conflictIds.size > 0;

  if (resources.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center justify-center py-24 gap-3">
        <Calendar size={40} className="text-slate-200" />
        <p className="text-slate-400 font-medium">{t('Tidak ada jadwal di hari ini', 'No bookings today')}</p>
        <p className="text-slate-300 text-sm">{currentDate.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* ── Conflict Alert Banner ─────────────────────────────────────────── */}
      {hasConflicts && (
        <div data-testid="conflict-alert-banner"
          className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
          <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center">
            <AlertTriangle size={16} className="text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-red-800">
              {t('Konflik Jadwal Terdeteksi', 'Booking Conflict Detected')}
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              {conflictIds.size} {t('pesanan berebut unit/sumber daya yang sama di waktu bersamaan. Segera ubah jadwal.', 'bookings are sharing the same unit at overlapping times. Please reschedule.')}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {/* Header row: time + resource names */}
        <div className="flex border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
          <div className="w-16 flex-shrink-0 border-r border-slate-100" />
          {resources.map(r => {
            const hasResourceConflict = dayEvents.filter(e => e.resource === r).some(e => conflictIds.has(e.id));
            return (
              <div key={r} className={`flex-1 text-center py-3 border-r last:border-r-0 border-slate-100 min-w-[120px] ${hasResourceConflict ? 'bg-red-50' : ''}`}>
                <p className={`text-sm font-bold font-manrope truncate px-2 ${hasResourceConflict ? 'text-red-700' : 'text-slate-800'}`}>{r}</p>
                <p className={`text-xs ${hasResourceConflict ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                  {dayEvents.filter(e => e.resource === r).length} {t('booking', 'booking')}
                  {hasResourceConflict && ` · ⚠ konflik`}
                </p>
              </div>
            );
          })}
        </div>

        {/* Timeline grid */}
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
          <div className="flex" style={{ minHeight: TOTAL_PX }}>
            {/* Hour labels */}
            <div className="w-16 flex-shrink-0 relative border-r border-slate-100" style={{ height: TOTAL_PX }}>
              {HOURS.map(h => (
                <div key={h} style={{ position: 'absolute', top: (h - START_H) * HOUR_PX - 9, right: 0 }}
                  className="w-full text-right pr-2 text-xs text-slate-400 select-none">
                  {String(h).padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Resource columns */}
            {resources.map(r => {
              const colEvents = dayEvents.filter(e => e.resource === r);
              const hasResourceConflict = colEvents.some(e => conflictIds.has(e.id));
              return (
                <div key={r}
                  className={`flex-1 relative border-r last:border-r-0 min-w-[120px] ${hasResourceConflict ? 'bg-red-50/30 border-red-100' : 'border-slate-50'}`}
                  style={{ height: TOTAL_PX }}>
                  {/* Hour grid lines */}
                  {Array.from({ length: TOTAL_H }, (_, i) => (
                    <div key={i} style={{ position: 'absolute', top: i * HOUR_PX, height: HOUR_PX }}
                      className="w-full border-t border-slate-100" />
                  ))}

                  {/* Events */}
                  {colEvents.map(e => {
                    const pos = getEventPos(e.start, e.end);
                    if (!pos) return null;
                    const isConflict = conflictIds.has(e.id);
                    const c = getStyle(e);
                    const isShort = pos.height < 48;
                    return (
                      <div key={e.id} onClick={() => onOrderClick(e.order_id)}
                        data-testid={`calendar-event-${e.id}${isConflict ? '-conflict' : ''}`}
                        style={{ position: 'absolute', top: pos.top + 2, height: pos.height - 4, left: 3, right: 3 }}
                        className={`rounded-lg px-2 py-1 cursor-pointer hover:shadow-lg transition-all overflow-hidden group
                          ${isConflict
                            ? 'bg-red-100 border-l-4 border-l-red-500 text-red-900 ring-1 ring-red-300'
                            : `${c.bg} border-l-4 ${c.bg.replace('bg-', 'border-l-').replace('-100', '-500')} ${c.text}`
                          }`}>
                        {isConflict && (
                          <div className="flex items-center gap-1 mb-0.5">
                            <AlertTriangle size={10} className="text-red-500 flex-shrink-0" />
                            <span className="text-red-500 text-[10px] font-bold uppercase tracking-wide">Konflik</span>
                          </div>
                        )}
                        <p className="text-xs font-extrabold font-manrope truncate">{e.customer_name}</p>
                        {!isShort && (
                          <>
                            <p className="text-xs opacity-75 truncate">{fmtTime(parseDT(e.start))} – {fmtTime(parseDT(e.end))}</p>
                            {e.tracking_code && <p className="text-xs opacity-50 truncate">{e.tracking_code}</p>}
                          </>
                        )}
                        {isShort && <p className="text-xs opacity-60 truncate">{fmtTime(parseDT(e.start))}</p>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main CalendarCore ────────────────────────────────────────────────────────

export default function CalendarCore() {
  const { t } = useLanguage();
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  const { start, end } = useMemo(() => getQueryRange(view, currentDate), [view, currentDate]);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['calendar-events', start, end],
    queryFn: () => calendarApi.getEvents(start, end).then(r => r.data),
  });

  const isAdmin = ['owner', 'manager'].includes(userRole);
  const orderBasePath = isAdmin ? '/admin/orders' : '/karyawan/orders';

  function navigate_date(delta) {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + delta);
    else if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta);
    setCurrentDate(d);
  }

  function onDayClick(day) {
    setCurrentDate(day);
    setView('day');
  }

  function onOrderClick(orderId) {
    navigate(`${orderBasePath}/${orderId}`);
  }

  const headerTitle = () => {
    if (view === 'month') return currentDate.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const mon = getMonday(currentDate);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return `${mon.getDate()} – ${sun.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const VIEWS = [
    { key: 'month', label: t('Bulan', 'Month'), icon: Grid3X3 },
    { key: 'week', label: t('Minggu', 'Week'), icon: List },
    { key: 'day', label: t('Hari', 'Day'), icon: Calendar },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Kalender', 'Calendar')}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{t('Jadwal Rental & Appointment', 'Rental & Appointment Schedule')}</p>
        </div>

        {/* View switcher */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          {VIEWS.map(v => {
            const Icon = v.icon;
            return (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`calendar-view-${v.key}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all
                  ${view === v.key ? 'bg-white text-[#0f6c44] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Icon size={14} />{v.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm px-4 py-3">
        <button onClick={() => navigate_date(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-colors text-slate-600">
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <h2 className="font-extrabold text-slate-800 font-manrope text-base capitalize">{headerTitle()}</h2>
          {isLoading && <p className="text-xs text-slate-400 animate-pulse">Memuat...</p>}
          {!isLoading && <p className="text-xs text-slate-400">{events.length} {t('jadwal', 'bookings')}</p>}
        </div>
        <button onClick={() => navigate_date(1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-colors text-slate-600">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 items-center">
        {[
          { dot: 'bg-amber-500', label: t('Rental', 'Rental') },
          { dot: 'bg-purple-500', label: t('Appointment', 'Appointment') },
        ].map(({ dot, label }) => (
          <div key={label} className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className={`w-2.5 h-2.5 rounded-full ${dot}`} />
            {label}
          </div>
        ))}
        {view === 'day' && (
          <div className="flex items-center gap-1.5 text-xs text-red-500 font-medium">
            <AlertTriangle size={11} />
            {t('Merah = ada konflik waktu', 'Red = time conflict')}
          </div>
        )}
        {view === 'day' && (
          <p className="text-xs text-slate-400">{t('Klik event untuk lihat detail pesanan', 'Click event to view order detail')}</p>
        )}
      </div>

      {/* Calendar view */}
      {view === 'month' && <MonthView events={events} currentDate={currentDate} onDayClick={onDayClick} />}
      {view === 'week' && <WeekView events={events} currentDate={currentDate} onDayClick={onDayClick} onOrderClick={onOrderClick} />}
      {view === 'day' && <DayView events={events} currentDate={currentDate} onOrderClick={onOrderClick} />}
    </div>
  );
}
